import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { useRequiredAuth } from "../../common/guard/requiredAuth";
import {
  DashboardBarChart,
  DashboardDoughnutChart,
  DashboardLineChart,
  DashboardMultiSeriesPieChart,
  DashboardPolarAreaChart,
  DashboardRadarChart,
} from "../../common/components/CustomizedCharts";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Tab,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";
import ShowChartRoundedIcon from "@mui/icons-material/ShowChartRounded";
import StackedBarChartRoundedIcon from "@mui/icons-material/StackedBarChartRounded";
import LazyLoader from "../../common/components/LazyLoader";
import ListAltRoundedIcon from "@mui/icons-material/ListAltRounded";
import DonutLargeRoundedIcon from "@mui/icons-material/DonutLargeRounded";
import LegendToggleRoundedIcon from "@mui/icons-material/LegendToggleRounded";
import TrackChangesRoundedIcon from "@mui/icons-material/TrackChangesRounded";
import RadarRoundedIcon from "@mui/icons-material/RadarRounded";
import MultilineChartRoundedIcon from "@mui/icons-material/MultilineChartRounded";
import TroubleshootRoundedIcon from "@mui/icons-material/TroubleshootRounded";
import BubbleChartRoundedIcon from "@mui/icons-material/BubbleChartRounded";

import {
  getRBMYearlyTrending,
  getAdvertisedPostsAndHiresTrending,
  getRetirementProjection,
  getLeavePlanTrend,
  getTrainingRequestTrend,
  getPayrollExpenditureTrend,
} from "../../../store/reporting/actions";
import { chartColors } from "../../common/components/Utils";
import StructureDashboard from "../../admin/components/StructureDashboard";
import RecruitmentDashboard from "../../admin/components/RecruitmentDashboard";
import PayrollDashboard from "../../payroll/pages/PayrollDashboard";
import TrainingDashboard from "../../training/components/TrainingDashboard";
import ExitDashboard from "../../admin/components/ExitDashboard";

const ReportingDashboard = (props) => {
  const {
    user,
    loading,
    getRetirementProjection,
    getRBMYearlyTrending,
    getAdvertisedPostsAndHiresTrending,
    getLeavePlanTrend,
    getTrainingRequestTrend,
    getPayrollExpenditureTrend,
  } = props;
  const { hasPermissions } = useRequiredAuth();
  const isForOversight = hasPermissions([
    "IS_ADMIN",
    "CAN_ACCESS_OVERSIGHT_REPORTING",
  ]);

  const [tabValue, setTabValue] = useState("exit");

  const [loadingData, setLoadingData] = useState({
    retirementProjection: true,
    rbmYTreding: true,
    postsAndHiredTrending: true,
    leavePlanTrending: true,
    trainingRequestTrending: true,
    payrollExpenditure: true,
  });
  const [retirementProjectionData, setRetirementProjectionData] =
    useState(null);
  const [retirementProjection, setRetirementProjection] = useState(null);
  const [retirementChartOptions, setRetirementChartOption] = useState({
    chartType: "bar",
    isHorizontal: false,
    isStacked: true,
  });
  const [rbmYTredingData, setRBMYTredingData] = useState(null);
  const [rbmYTredingChartOptions, setRBMYTredingChartOption] = useState({
    rbmYTredingData: null,
    chartType: "bar",
    isHorizontal: false,
    isStacked: false,
  });
  const [postsAndHiredTrendingData, setPostsAndHiredTrendingData] =
    useState(null);
  const [postAndHiredTrendingChartOptions, setPostAndHiredTrendingChartOption] =
    useState({
      chartType: "bar",
      isHorizontal: false,
      isStacked: false,
    });
  const [leavePlanTrendData, setLeavePlanTrendData] = useState(null);
  const [leavePlanTrendChartOption, setLeavePlanTrendChartOption] = useState({
    chartType: "bar",
    isHorizontal: false,
    isStacked: false,
  });
  const [trainingRequestData, setTrainingRequestData] = useState(null);
  const [trainingRequestTrendChartOption, setTrainingRequestTrendChartOption] =
    useState({
      chartType: "bar",
      isHorizontal: false,
      isStacked: true,
    });
  const [payrollExpenditureData, setPayrollExpenditureData] = useState(null);
  const [
    payrollExpenditureTrendChartOption,
    setPayrollExpenditureTrendChartOption,
  ] = useState({
    chartType: "bar",
    isHorizontal: false,
    isStacked: false,
  });

  const handleLoadData = (action = "retirement-projection") => {
    const query = {
      unitId: "",
      entitySectorId: "",
      planningEntityId: "",
      fiscalYearId: "",
      selectedYears: "",
      retirementType: "",
    };
    if (action === "retirement-projection") {
      getRetirementProjection(query, (status, data) => {
        setRetirementProjectionData(data);
        setLoadingData((prev) => ({ ...prev, retirementProjection: status }));
      });
    } else if (action === "rbm-yTrending") {
      getRBMYearlyTrending(query, (status, data) => {
        setRBMYTredingData(data);
        setLoadingData((prev) => ({ ...prev, rbmYTreding: status }));
      });
    } else if (action === "posts-hired-trending") {
      getAdvertisedPostsAndHiresTrending(query, (status, data) => {
        setPostsAndHiredTrendingData(data);
        setLoadingData((prev) => ({ ...prev, postsAndHiredTrending: status }));
      });
    } else if (action === "leave-plan-trending") {
      getLeavePlanTrend(query, (status, data) => {
        setLeavePlanTrendData(data);
        setLoadingData((prev) => ({ ...prev, leavePlanTrending: status }));
      });
    } else if (action === "training-request-trending") {
      getTrainingRequestTrend(query, (status, data) => {
        setTrainingRequestData(data);
        setLoadingData((prev) => ({
          ...prev,
          trainingRequestTrending: status,
        }));
      });
    } else if (action === "payroll-expenditure-trending") {
      getPayrollExpenditureTrend(query, (status, data) => {
        setPayrollExpenditureData(data);
        setLoadingData((prev) => ({
          ...prev,
          payrollExpenditure: status,
        }));
      });
    }
  };

  // useEffect(() => {
  //   handleLoadData("posts-hired-trending");
  // }, []);

  const handleChange = (e, newValue) => {
    setTabValue(newValue);
  };

  useEffect(() => {
    if (!!retirementProjectionData) {
      if (retirementChartOptions.chartType === "bar") {
        setRetirementProjection(
          formatRetirementData(retirementProjectionData, "combo-chart")
        );
      } else if (retirementChartOptions.chartType === "bar") {
        setRetirementProjection(
          formatRetirementData(retirementProjectionData, "grouped-line")
        );
      } else if (retirementChartOptions.chartType === "list") {
        setRetirementProjection(
          formatRetirementData(retirementProjectionData, "table")
        );
      }
    }
  }, [retirementProjectionData, retirementChartOptions.chartType]);

  return (
    <>
      <div className="container-fluid">
        {false && (
          <div className="border rounded dashboard-card elevated mt-2 mx-4 mb-4">
            <div className="row justify-content-center pb-2 pt-3 px-3">
              <div className="elevation-2 col-12 col-lg-2 col-md-3 col-sm-4">
                <Autocomplete
                  size="small"
                  id="selectedUnit"
                  defaultValue={null}
                  value={null}
                  options={[
                    {
                      unitId: null,
                      unitName: "All",
                    },
                    // ...sortedUnits,
                  ]}
                  isOptionEqualToValue={(option, value) =>
                    option.unitId === value.unitId
                  }
                  onChange={(event, selectedUnit) => {
                    // setSelectedFilters({
                    //   ...selectedFilters,
                    //   unit: selectedUnit || null,
                    // });
                    // setSearchFilter(true);
                  }}
                  getOptionLabel={(option) => option.unitName}
                  renderOption={(props, selectedUnit) => (
                    <Box component="li" {...props} key={selectedUnit.unitId}>
                      {selectedUnit.unitName}
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Unit"
                      placeholder="Unit"
                      inputProps={{
                        ...params.inputProps,
                        // autoComplete: "selectedUnit",
                      }}
                    />
                  )}
                />
              </div>
              <div className="col-12 col-lg-2 col-md-3 col-sm-4 mt-3 mt-sm-0">
                <Autocomplete
                  size="small"
                  id="selectedEmployeeGroup"
                  defaultValue={null}
                  value={null}
                  options={[
                    {
                      id: null,
                      name: "All",
                    },

                    // ...(employeeGroups || []),
                  ]}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  onChange={(event, selectedGroup) => {
                    // setSelectedFilters({
                    //   ...selectedFilters,
                    //   employeeGroup: selectedGroup || null,
                    // });
                    // setSearchFilter(true);
                  }}
                  getOptionLabel={(option) => option.name}
                  renderOption={(props, selectedGroup) => (
                    <Box component="li" {...props}>
                      {selectedGroup.name}
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Employee Groups"
                      placeholder="Employee Groups"
                      inputProps={{
                        ...params.inputProps,
                      }}
                    />
                  )}
                />
              </div>
              <div className="col-12 col-lg-3 col-md-3 col-sm-4 mt-2 mt-sm-0">
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <div className="d-flex align-items-center mt-n1">
                    <DesktopDatePicker
                      label="Leave Start Date"
                      inputFormat="dd/MM/yyyy"
                      // disabled={!!showReport && reportPeriod !== ""}
                      // value={selectedFilters.dateRange.startDate}
                      onChange={(date) => {
                        // setSelectedFilters((prev) => ({
                        //   ...prev,
                        //   dateRange: {
                        //     startDate: date,
                        //     endDate: null,
                        //   },
                        // }));
                        // setSearchFilter(true);
                      }}
                      renderInput={(params) => (
                        <TextField
                          onKeyDown={(e) => e.preventDefault()}
                          required={true}
                          fullWidth
                          size="small"
                          {...params}
                        />
                      )}
                      // disablePast={!selectedPlan && !leaveToPostpone}
                    />

                    <span className="mx-2" style={{ fontSize: "30px" }}>
                      -
                    </span>

                    <DesktopDatePicker
                      label="Leave End Date"
                      inputFormat="dd/MM/yyyy"
                      // disabled={!!showReport && reportPeriod !== ""}
                      // value={selectedFilters.dateRange.endDate}
                      // onChange={(date) => {
                      //   setSelectedFilters((prev) => ({
                      //     ...prev,
                      //     dateRange: {
                      //       startDate: prev.dateRange.startDate,
                      //       endDate: date,
                      //     },
                      //   }));
                      //   setSearchFilter(true);
                      // }}
                      renderInput={(params) => (
                        <TextField
                          onKeyDown={(e) => e.preventDefault()}
                          required={true}
                          fullWidth
                          size="small"
                          {...params}
                        />
                      )}
                      // disablePast={!selectedPlan && !leaveToPostpone}
                    />
                  </div>
                </LocalizationProvider>
              </div>
              <div className="col-12 d-flex align-items-center justify-content-center mt-2">
                <FormControl
                // disabled={loadingData.report}
                >
                  <RadioGroup
                    row
                    aria-labelledby="report-filter-periods-label"
                    name="report-filter-periods"
                    // value={reportPeriod}
                    // onChange={(e) => setReportPeriod(e.target.value)}
                  >
                    <FormControlLabel
                      value="this-year"
                      control={<Radio />}
                      label="This Year"
                    />
                    <FormControlLabel
                      value="this-month"
                      control={<Radio />}
                      label="This Month"
                    />
                    <FormControlLabel
                      value="this-week"
                      control={<Radio />}
                      label="This Week"
                    />
                    <FormControlLabel
                      value="today"
                      control={<Radio />}
                      label="To Day"
                    />
                    <FormControlLabel
                      value=""
                      control={<Radio />}
                      label="Open"
                    />
                  </RadioGroup>
                </FormControl>
              </div>

              <div className="col-12 d-flex align-items-center justify-content-center mt-2">
                {/* {!!showFilter && ( */}
                <Button
                  variant="outlined"
                  size="small"
                  className="mr-3"
                  color="inherit"
                  // startIcon={<FilterAltOffRoundedIcon />}
                  // onClick={() => {
                  //   setSelectedFilters(defaultFilterValue);
                  //   handleLoadingData({
                  //     unitId: "",
                  //     employeeGroupId: "",
                  //     startDate: "",
                  //     endDate: "",
                  //   });
                  // }}
                  // disabled={
                  //   !selectedFilters.unit?.unitId &&
                  //   !selectedFilters.employeeGroup?.id &&
                  //   !selectedFilters.dateRange.startDate &&
                  //   !selectedFilters.dateRange.endDate
                  // }
                >
                  Cancel
                </Button>
                {/* )}
                      {!!showFilter && ( */}
                <Button
                  variant="contained"
                  size="small"
                  endIcon={<FilterAltRoundedIcon />}
                  // disabled={!searchFilter}
                  // onClick={() => handleLoadingData()}
                >
                  Search
                </Button>
                {/* )} */}
              </div>
            </div>
          </div>
        )}

        <div className="row justify-content-center tmis-shadow rounded bg-light py-3">
          <div className="col-12 mb-3">
            <Tabs
              value={tabValue}
              onChange={handleChange}
              variant="scrollable"
              scrollButtons="auto"
              aria-label="modules dashboards"
              className="mx-0 mx-sm-3"
            >
              <Tab label="Overview" value={"overview"} />
              <Tab label="Structure" value={"structure"} />
              <Tab label="Recruitment" value={"recruitment"} />
              <Tab label="Payroll" value={"payroll"} />
              <Tab label="RBM" value={"rbm"} />
              <Tab label="Leave & Absence" value={"leave"} />
              <Tab label="Training & Authorization" value={"training"} />
              <Tab label="Exit & Termination" value={"exit"} />
            </Tabs>
          </div>

          <div className="col-12">
            {tabValue === "overview" && (
              <div className="row">
                {/* Exit & Termination CHART */}
                <div className="col-12 col-sm-6 col-md-4 mb-4">
                  <div className="border rounded p-2">
                    <div className="d-flex align-items-center justify-content-end mb-2">
                      <ToggleButtonGroup
                        value={retirementChartOptions.chartType}
                        exclusive
                        onChange={(event, newType) =>
                          setRetirementChartOption((prev) => ({
                            ...prev,
                            chartType: newType,
                          }))
                        }
                        size="small"
                        color="primary"
                        aria-label="chart type"
                        disabled={loadingData.retirementProjection}
                      >
                        <ToggleButton value="bar" aria-label="combo bar chart">
                          <StackedBarChartRoundedIcon fontSize="inherit" />
                        </ToggleButton>
                        <ToggleButton value="line" aria-label="line chart">
                          <ShowChartRoundedIcon fontSize="inherit" />
                        </ToggleButton>
                        <ToggleButton value="list" aria-label="table data">
                          <ListAltRoundedIcon fontSize="inherit" />
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </div>
                    <LazyLoader
                      data={retirementProjectionData}
                      fetchData={handleLoadData}
                    >
                      <div className="border rounded dashboard-card">
                        {retirementChartOptions.chartType === "bar" && (
                          <DashboardBarChart
                            chartData={formatRetirementData(
                              retirementProjectionData,
                              "combo-chart"
                            )}
                            loading={
                              loadingData.retirementProjection ||
                              !retirementProjectionData
                            }
                            chartTitle="Exit & Termination (Retirement Trend, Retirement Projection in 5 Years)"
                            isStackedChart={retirementChartOptions.isStacked}
                            isHorizontalBar={
                              retirementChartOptions.isHorizontal
                            }
                            enableTooltipEnhancements
                            enableDataLabels
                            ignoreBorder
                          />
                        )}

                        {retirementChartOptions.chartType === "line" && (
                          <DashboardLineChart
                            chartData={formatRetirementData(
                              retirementProjectionData,
                              "grouped-line"
                            )}
                            loading={
                              loadingData.retirementProjection ||
                              !retirementProjectionData
                            }
                            chartTitle="Exit & Termination (Retirement Trend, Retirement Projection in 5 Years)"
                            // ignoreBorder
                          />
                        )}

                        {retirementChartOptions.chartType === "list" &&
                          retirementProjection[0] && (
                            <div>
                              <TableContainer
                                component={Paper}
                                sx={{ maxHeight: 440, overflow: "hidden" }}
                              >
                                <TableContainer
                                  size="small"
                                  aria-label="Quarter Score Average monthly counts"
                                  stickyHeader
                                >
                                  <TableHead>
                                    <TableRow>
                                      {(
                                        Object.keys(
                                          retirementProjection[0] || {}
                                        ) || []
                                      ).map((label, index) => (
                                        <TableCell
                                          key={index + label}
                                          // component={index === 0 ? "th" : ""}
                                          align={index === 0 ? "" : "right"}
                                        >
                                          {label}
                                        </TableCell>
                                      ))}
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {(retirementProjection || []).map(
                                      (item, index) => (
                                        <TableRow
                                          key={index}
                                          sx={{
                                            "&:last-child td, &:last-child th":
                                              {
                                                border: 0,
                                              },
                                          }}
                                        >
                                          {Object.keys(
                                            retirementProjection[0]
                                          ).map((header, hIndex) => (
                                            <TableCell
                                              key={header}
                                              component={
                                                hIndex === 0 ? "th" : ""
                                              }
                                              scope={hIndex === 0 ? "row" : ""}
                                              align={
                                                hIndex === 0 ? "" : "right"
                                              }
                                            >
                                              {item[header]}
                                            </TableCell>
                                          ))}
                                        </TableRow>
                                      )
                                    )}
                                  </TableBody>
                                </TableContainer>
                              </TableContainer>
                            </div>
                          )}
                      </div>
                    </LazyLoader>
                  </div>
                </div>

                {/* RBM Average Performance Trend CHART */}
                <div className="col-12 col-sm-6 col-md-4 mb-4">
                  <div className="border rounded p-2">
                    <div className="d-flex align-items-center justify-content-end mb-2">
                      <ToggleButtonGroup
                        value={rbmYTredingChartOptions.chartType}
                        exclusive
                        onChange={(event, newType) =>
                          setRBMYTredingChartOption((prev) => ({
                            ...prev,
                            chartType: newType,
                          }))
                        }
                        size="small"
                        color="primary"
                        aria-label="chart type"
                        disabled={loadingData.rbmYTreding}
                      >
                        <ToggleButton value="bar" aria-label="combo bar chart">
                          <StackedBarChartRoundedIcon fontSize="inherit" />
                        </ToggleButton>
                        <ToggleButton value="line" aria-label="line chart">
                          <ShowChartRoundedIcon fontSize="inherit" />
                        </ToggleButton>
                        <ToggleButton value="pie" aria-label="table data">
                          <DonutLargeRoundedIcon fontSize="inherit" />
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </div>
                    {/* RBM - Average performance Trend */}
                    <LazyLoader
                      data={rbmYTredingData}
                      fetchData={() => handleLoadData("rbm-yTrending")}
                    >
                      <div className="border rounded dashboard-card">
                        {rbmYTredingChartOptions.chartType === "bar" && (
                          <DashboardBarChart
                            chartTitle="RBM - Average performance Trend"
                            chartData={formatRBMChartData(
                              rbmYTredingData,
                              "grouped"
                            )}
                            loading={
                              loadingData.rbmYTreding || !rbmYTredingData
                            }
                            isStackedChart={rbmYTredingChartOptions.isStacked}
                            isHorizontalBar={
                              rbmYTredingChartOptions.isHorizontal
                            }
                            ignoreBorder
                            enableTooltipEnhancements
                            enableDataLabels
                          />
                        )}

                        {rbmYTredingChartOptions.chartType === "line" && (
                          <DashboardLineChart
                            chartTitle="RBM - Average performance Trend"
                            chartData={formatRBMChartData(
                              rbmYTredingData,
                              "line"
                            )}
                            loading={
                              loadingData.rbmYTreding || !rbmYTredingData
                            }
                            // ignoreBorder
                          />
                        )}

                        {rbmYTredingChartOptions.chartType === "pie" && (
                          <DashboardDoughnutChart
                            chartTitle="RBM - Average performance Trend"
                            chartData={formatRBMChartData(
                              rbmYTredingData,
                              rbmYTredingChartOptions.chartType
                            )}
                            loading={
                              loadingData.rbmYTreding || !rbmYTredingData
                            }
                            centerDisplay="none"
                            enableDataLabels
                          />
                        )}
                      </div>
                    </LazyLoader>
                  </div>
                </div>

                {/* Recruitment - Advertised posts and hired employees’ trend */}
                <div className="col-12 col-sm-6 col-md-4 mb-4">
                  <div className="border rounded p-2">
                    <div className="d-flex align-items-center justify-content-end mb-2">
                      <ToggleButtonGroup
                        value={postAndHiredTrendingChartOptions.chartType}
                        exclusive
                        onChange={(event, newType) =>
                          setPostAndHiredTrendingChartOption((prev) => ({
                            ...prev,
                            chartType: newType,
                          }))
                        }
                        size="small"
                        color="primary"
                        aria-label="chart type"
                        disabled={loadingData.postsAndHiredTrending}
                      >
                        <ToggleButton
                          value="bar"
                          aria-label="grouped bar chart"
                        >
                          <StackedBarChartRoundedIcon fontSize="inherit" />
                        </ToggleButton>
                        <ToggleButton
                          value="combo"
                          aria-label="Combo Grouped Bar chart"
                        >
                          <LegendToggleRoundedIcon fontSize="inherit" />
                        </ToggleButton>
                        <ToggleButton value="line" aria-label="line chart">
                          <ShowChartRoundedIcon fontSize="inherit" />
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </div>
                    <LazyLoader
                      data={postsAndHiredTrendingData}
                      fetchData={() => handleLoadData("posts-hired-trending")}
                    >
                      <div className="border rounded dashboard-card">
                        {postAndHiredTrendingChartOptions.chartType ===
                          "bar" && (
                          <DashboardBarChart
                            chartTitle="Recruitment - Advertised posts and hired employees’ trend"
                            chartData={formatRecruitmentTrendData(
                              postsAndHiredTrendingData,
                              "bar"
                            )}
                            loading={
                              loadingData.postsAndHiredTrending ||
                              !postsAndHiredTrendingData
                            }
                            isStackedChart={
                              postAndHiredTrendingChartOptions.isStacked
                            }
                            isHorizontalBar={
                              postAndHiredTrendingChartOptions.isHorizontal
                            }
                            ignoreBorder
                            enableTooltipEnhancements
                            enableDataLabels
                          />
                        )}

                        {postAndHiredTrendingChartOptions.chartType ===
                          "combo" && (
                          <DashboardBarChart
                            chartTitle="Recruitment - Advertised posts and hired employees’ trend"
                            chartData={formatRecruitmentTrendData(
                              postsAndHiredTrendingData,
                              "combo"
                            )}
                            loading={
                              loadingData.postsAndHiredTrending ||
                              !postsAndHiredTrendingData
                            }
                            isStackedChart={
                              postAndHiredTrendingChartOptions.isStacked
                            }
                            isHorizontalBar={
                              postAndHiredTrendingChartOptions.isHorizontal
                            }
                            ignoreBorder
                            enableTooltipEnhancements
                            // enableDataLabels
                          />
                        )}

                        {postAndHiredTrendingChartOptions.chartType ===
                          "line" && (
                          <DashboardLineChart
                            chartTitle="Recruitment - Advertised posts and hired employees’ trend"
                            chartData={formatRecruitmentTrendData(
                              postsAndHiredTrendingData,
                              "line"
                            )}
                            loading={
                              loadingData.postsAndHiredTrending ||
                              !postsAndHiredTrendingData
                            }
                            // ignoreBorder
                          />
                        )}
                      </div>
                    </LazyLoader>
                  </div>
                </div>

                {/* Leave and Absence - Trend of planed and taken annual leaves */}
                <div className="col-12 col-sm-6 col-md-4 mb-4">
                  <div className="border rounded p-2">
                    <div className="d-flex align-items-center justify-content-end mb-2">
                      <ToggleButtonGroup
                        value={leavePlanTrendChartOption.chartType}
                        exclusive
                        onChange={(event, newType) =>
                          setLeavePlanTrendChartOption((prev) => ({
                            ...prev,
                            chartType: newType,
                          }))
                        }
                        size="small"
                        color="primary"
                        aria-label="chart type"
                        disabled={loadingData.leavePlanTrending}
                      >
                        <ToggleButton
                          value="bar"
                          aria-label="grouped bar chart"
                        >
                          <StackedBarChartRoundedIcon fontSize="inherit" />
                        </ToggleButton>
                        <ToggleButton
                          value="combo"
                          aria-label="Combo Grouped Bar chart"
                        >
                          <LegendToggleRoundedIcon fontSize="inherit" />
                        </ToggleButton>
                        <ToggleButton value="line" aria-label="line chart">
                          <ShowChartRoundedIcon fontSize="inherit" />
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </div>
                    <LazyLoader
                      data={leavePlanTrendData}
                      fetchData={() => handleLoadData("leave-plan-trending")}
                    >
                      <div className="border rounded dashboard-card">
                        {leavePlanTrendChartOption.chartType === "bar" && (
                          <DashboardBarChart
                            chartTitle="Leave and Absence - Trend of planed and taken annual leaves"
                            chartData={formatLeavePlanTrendData(
                              leavePlanTrendData,
                              "bar"
                            )}
                            loading={
                              loadingData.leavePlanTrending ||
                              !leavePlanTrendData
                            }
                            isStackedChart={leavePlanTrendChartOption.isStacked}
                            isHorizontalBar={
                              leavePlanTrendChartOption.isHorizontal
                            }
                            enableTooltipEnhancements
                            enableDataLabels
                            tooltipFormatter={(context) => {
                              const label = context.dataset.label;
                              const value = context.raw;
                              const index = context.dataIndex;

                              const planned =
                                context.chart.data.datasets[0].data[index];
                              const taken =
                                context.chart.data.datasets[1]?.data[index] ??
                                0;
                              const diff = planned - taken;
                              const percent = planned
                                ? ((taken / planned) * 100).toFixed(1)
                                : 0;

                              return label.includes("Taken")
                                ? [
                                    `Taken: ${value}`,
                                    `Difference: ${diff}`,
                                    `Utilization: ${percent}%`,
                                  ]
                                : `${label}: ${value}`;
                            }}
                            ignoreBorder
                          />
                        )}

                        {leavePlanTrendChartOption.chartType === "combo" && (
                          <DashboardBarChart
                            chartTitle="Leave and Absence - Trend of planed and taken annual leaves"
                            chartData={formatLeavePlanTrendData(
                              leavePlanTrendData,
                              "combo"
                            )}
                            loading={
                              loadingData.leavePlanTrending ||
                              !leavePlanTrendData
                            }
                            isStackedChart={leavePlanTrendChartOption.isStacked}
                            isHorizontalBar={
                              leavePlanTrendChartOption.isHorizontal
                            }
                            ignoreBorder
                            enableTooltipEnhancements
                            enableDataLabels
                            tooltipFormatter={(context) => {
                              const label = context.dataset.label;
                              const value = context.raw;
                              const index = context.dataIndex;

                              const planned =
                                context.chart.data.datasets[0].data[index];
                              const taken =
                                context.chart.data.datasets[1]?.data[index] ??
                                0;
                              const diff = planned - taken;
                              const percent = planned
                                ? ((taken / planned) * 100).toFixed(1)
                                : 0;

                              return label.includes("Taken")
                                ? [
                                    `Taken: ${value}`,
                                    `Difference: ${diff}`,
                                    `Utilization: ${percent}%`,
                                  ]
                                : `${label}: ${value}`;
                            }}
                          />
                        )}

                        {leavePlanTrendChartOption.chartType === "line" && (
                          <DashboardLineChart
                            chartTitle="Leave and Absence - Trend of planed and taken annual leaves"
                            chartData={formatLeavePlanTrendData(
                              leavePlanTrendData,
                              "line"
                            )}
                            loading={
                              loadingData.leavePlanTrending ||
                              !leavePlanTrendData
                            }
                            // ignoreBorder
                          />
                        )}
                      </div>
                    </LazyLoader>
                  </div>
                </div>

                {/* Training - Trend of Requested and Approved training requests */}
                <div className="col-12 col-sm-6 col-md-4 mb-4">
                  <div className="border rounded p-2">
                    <div className="d-flex align-items-center justify-content-end mb-2">
                      <ToggleButtonGroup
                        value={trainingRequestTrendChartOption.chartType}
                        exclusive
                        onChange={(event, newType) =>
                          setTrainingRequestTrendChartOption((prev) => ({
                            ...prev,
                            chartType: newType,
                          }))
                        }
                        size="small"
                        color="primary"
                        aria-label="chart type"
                        disabled={loadingData.trainingRequestTrending}
                      >
                        <ToggleButton
                          value="bar"
                          aria-label="grouped bar chart"
                        >
                          <StackedBarChartRoundedIcon fontSize="inherit" />
                        </ToggleButton>
                        <ToggleButton
                          value="line"
                          aria-label="multi line chart"
                        >
                          <ShowChartRoundedIcon fontSize="inherit" />
                        </ToggleButton>
                        <ToggleButton
                          value="pie"
                          aria-label="doughnut chart data"
                        >
                          <DonutLargeRoundedIcon fontSize="inherit" />
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </div>
                    <LazyLoader
                      data={trainingRequestData}
                      fetchData={() =>
                        handleLoadData("training-request-trending")
                      }
                    >
                      <div className="border rounded dashboard-card">
                        {trainingRequestTrendChartOption.chartType ===
                          "bar" && (
                          <DashboardBarChart
                            chartTitle="Training - Trend of Requested and Approved training requests"
                            chartData={formatTrainingChartData(
                              trainingRequestData,
                              trainingRequestTrendChartOption.chartType
                            )}
                            loading={
                              loadingData.trainingRequestTrending ||
                              !trainingRequestData
                            }
                            isStackedChart={
                              trainingRequestTrendChartOption.isStacked
                            }
                            isHorizontalBar={
                              trainingRequestTrendChartOption.isHorizontal
                            }
                            enableTooltipEnhancements
                            enableDataLabels
                            tooltipFormatter={(context) => {
                              const { dataset, raw, dataIndex, chart } =
                                context;
                              const label = dataset.label ?? "";
                              const value = raw;

                              const approved =
                                chart.data.datasets[0].data[dataIndex] ?? 0;
                              const rejected =
                                chart.data.datasets[1]?.data[dataIndex] ?? 0;
                              const inProgress =
                                chart.data.datasets[2]?.data[dataIndex] ?? 0;

                              const total = approved + rejected + inProgress;
                              const percent = total
                                ? ((value / total) * 100).toFixed(1)
                                : 0;

                              return label.includes("In Progress")
                                ? [
                                    `${label}: ${value}`,
                                    `Share: ${percent}%`,
                                    `Year Total: ${total}`,
                                  ]
                                : [`${label}: ${value}`, `Share: ${percent}%`];
                            }}
                            ignoreBorder
                          />
                        )}

                        {trainingRequestTrendChartOption.chartType ===
                          "line" && (
                          <DashboardLineChart
                            chartTitle="Training - Trend of Requested and Approved training requests"
                            chartData={formatTrainingChartData(
                              trainingRequestData,
                              trainingRequestTrendChartOption.chartType
                            )}
                            loading={
                              loadingData.trainingRequestTrending ||
                              !trainingRequestData
                            }
                            // ignoreBorder
                          />
                        )}

                        {trainingRequestTrendChartOption.chartType ===
                          "pie" && (
                          <DashboardDoughnutChart
                            chartTitle="Training - Trend of Requested and Approved training requests"
                            chartData={formatTrainingChartData(
                              trainingRequestData,
                              trainingRequestTrendChartOption.chartType
                            )}
                            loading={
                              loadingData.trainingRequestTrending ||
                              !trainingRequestData
                            }
                            centerDisplay="total"
                            enableDataLabels
                          />
                        )}
                      </div>
                    </LazyLoader>
                  </div>
                </div>

                {/* Payroll - Total expenditure trend */}
                <div className="col-12 col-sm-6 col-md-4 mb-4">
                  <div className="border rounded p-2">
                    <div className="d-flex align-items-center justify-content-end mb-2">
                      <ToggleButtonGroup
                        value={payrollExpenditureTrendChartOption.chartType}
                        exclusive
                        onChange={(event, newType) =>
                          setPayrollExpenditureTrendChartOption((prev) => ({
                            ...prev,
                            chartType: newType,
                          }))
                        }
                        size="small"
                        color="primary"
                        aria-label="chart type"
                        disabled={loadingData.payrollExpenditure}
                      >
                        <ToggleButton value="bar" aria-label="Bar chart">
                          <StackedBarChartRoundedIcon fontSize="inherit" />
                        </ToggleButton>
                        <ToggleButton value="line" aria-label="line chart">
                          <ShowChartRoundedIcon fontSize="inherit" />
                        </ToggleButton>
                        <ToggleButton value="pie" aria-label="Pie">
                          <DonutLargeRoundedIcon fontSize="inherit" />
                        </ToggleButton>
                        <ToggleButton
                          value="polar-area"
                          aria-label="Polar Radar charts"
                        >
                          <MultilineChartRoundedIcon fontSize="inherit" />
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </div>
                    <LazyLoader
                      data={payrollExpenditureData}
                      fetchData={() =>
                        handleLoadData("payroll-expenditure-trending")
                      }
                    >
                      <div className="border rounded dashboard-card">
                        {payrollExpenditureTrendChartOption.chartType ===
                          "bar" && (
                          <DashboardBarChart
                            chartTitle="Payroll - Total expenditure trend"
                            chartData={formatPayrollChartData(
                              payrollExpenditureData,
                              payrollExpenditureTrendChartOption.chartType
                            )}
                            loading={
                              loadingData.payrollExpenditure ||
                              !payrollExpenditureData
                            }
                            isStackedChart={
                              payrollExpenditureTrendChartOption.isStacked
                            }
                            isHorizontalBar={
                              payrollExpenditureTrendChartOption.isHorizontal
                            }
                            ignoreBorder
                            enableDataLabels
                            enableTooltipEnhancements
                            isForMoney
                          />
                        )}

                        {payrollExpenditureTrendChartOption.chartType ===
                          "line" && (
                          <DashboardLineChart
                            chartTitle="Payroll - Total expenditure trend"
                            chartData={formatPayrollChartData(
                              payrollExpenditureData,
                              payrollExpenditureTrendChartOption.chartType
                            )}
                            loading={
                              loadingData.payrollExpenditure ||
                              !payrollExpenditureData
                            }
                            isForMoney
                          />
                        )}

                        {["pie", "doughnut"].includes(
                          payrollExpenditureTrendChartOption.chartType
                        ) && (
                          <DashboardDoughnutChart
                            chartTitle="Payroll - Total expenditure trend"
                            chartData={formatPayrollChartData(
                              payrollExpenditureData,
                              payrollExpenditureTrendChartOption.chartType
                            )}
                            loading={
                              loadingData.payrollExpenditure ||
                              !payrollExpenditureData
                            }
                            centerDisplay="total"
                            enableDataLabels
                            isForMoney
                          />
                        )}
                        {payrollExpenditureTrendChartOption.chartType ===
                          "polar-area" && (
                          <DashboardPolarAreaChart
                            chartTitle="Payroll - Total expenditure trend"
                            chartData={formatPayrollChartData(
                              payrollExpenditureData,
                              payrollExpenditureTrendChartOption.chartType
                            )}
                            loading={
                              loadingData.payrollExpenditure ||
                              !payrollExpenditureData
                            }
                            showTotal
                          />
                        )}
                      </div>
                    </LazyLoader>
                  </div>
                </div>
              </div>
            )}

            {tabValue === "structure" && <StructureDashboard />}
            {tabValue === "recruitment" && (
              <RecruitmentDashboard selectedYear="2025" />
            )}
            {tabValue === "payroll" && <PayrollDashboard isForOversight />}
            {tabValue === "training" && <TrainingDashboard />}
            {tabValue === "exit" && <ExitDashboard />}
          </div>
        </div>
      </div>
    </>
  );
};

const mapStateToProps = ({ user, loading }) => {
  return { user, loading };
};

export default connect(mapStateToProps, {
  getRetirementProjection,
  getRBMYearlyTrending,
  getAdvertisedPostsAndHiresTrending,
  getLeavePlanTrend,
  getTrainingRequestTrend,
  getPayrollExpenditureTrend,
})(ReportingDashboard);

const formatRetirementData = (data, chartType) => {
  if (!data || data.length === 0) {
    return chartType === "table" ? [] : { labels: [], datasets: [] };
  }

  const years = [...new Set(data.map((d) => d.retirementYear))];

  // Compute Totals for Labels
  const totalMale = data.reduce((sum, d) => sum + d.male, 0);
  const totalFemale = data.reduce((sum, d) => sum + d.female, 0);
  const total = data.reduce((sum, d) => sum + d.employees, 0);

  let formattedData = {
    labels: years,
    datasets: [],
  };

  if (chartType === "combo-chart") {
    return {
      labels: years,
      datasets: [
        {
          label: `Total Retirees (${total})`,
          type: "line",
          data: years.map((year) =>
            data
              .filter((d) => d.retirementYear === year)
              .reduce((sum, d) => sum + d.employees, 0)
          ),
          borderColor: chartColors["gold"].border,
          backgroundColor: chartColors["gold"].background,
          borderWidth: 2,
          fill: false,
          tension: 0.4,
        },
        {
          label: `Male Retirees (${totalMale})`,
          type: "bar",
          data: years.map((year) =>
            data
              .filter((d) => d.retirementYear === year)
              .reduce((sum, d) => sum + d.male, 0)
          ),
          backgroundColor: chartColors["sky-blue"].background,
          borderColor: chartColors["sky-blue"].border,
          borderWidth: 1,
        },
        {
          label: `Female Retirees (${totalFemale})`,
          type: "bar",
          data: years.map((year) =>
            data
              .filter((d) => d.retirementYear === year)
              .reduce((sum, d) => sum + d.female, 0)
          ),
          backgroundColor: chartColors["forest-green"].background,
          borderColor: chartColors["forest-green"].border,
          borderWidth: 1,
        },
      ],
    };
  }

  if (chartType === "bar" || chartType === "line") {
    formattedData.datasets = [
      {
        label: `Total Retirees (${total})`,
        data: years.map((year) =>
          data
            .filter((d) => d.retirementYear === year)
            .reduce((sum, d) => sum + d.employees, 0)
        ),
        backgroundColor: chartColors["sky-blue"].background,
        borderColor:
          chartType === "line" ? chartColors["sky-blue"].border : undefined,
        borderWidth: 2,
        fill: chartType === "line" ? false : undefined,
      },
    ];
  }

  if (chartType === "bar" || chartType === "grouped-line") {
    formattedData.datasets = [
      {
        label: `Male Retirees (${totalMale})`,
        data: years.map((year) =>
          data
            .filter((d) => d.retirementYear === year)
            .reduce((sum, d) => sum + d.male, 0)
        ),
        backgroundColor: chartColors["sky-blue"].background,
        borderColor:
          chartType === "grouped-line"
            ? chartColors["sky-blue"].border
            : undefined,
        borderWidth: 2,
        fill: chartType === "grouped-line" ? false : undefined,
      },
      {
        label: `Female Retirees (${totalFemale})`,
        data: years.map((year) =>
          data
            .filter((d) => d.retirementYear === year)
            .reduce((sum, d) => sum + d.female, 0)
        ),
        backgroundColor: chartColors["forest-green"].background,
        borderColor:
          chartType === "grouped-line"
            ? chartColors["forest-green"].border
            : undefined,
        borderWidth: 2,
        fill: chartType === "grouped-line" ? false : undefined,
      },
    ];
  }

  if (chartType === "table") {
    return data.map((d) => ({
      "Retirement Year": d.retirementYear,
      "Current Age": d.currentAge,
      Male: d.male,
      Female: d.female,
      "Total Employees": d.employees,
    }));
  }

  return formattedData;
};

const formatRBMChartData = (data, chartType) => {
  if (!data || data.length === 0) {
    return { labels: [], datasets: [] };
  }

  const labels = data.map((d) => d.fiscalYear);

  // Define performance categories
  const categories = [
    "indashyikirwa",
    "inkomezamihigo",
    "inkangurirwamihigo",
    "unspecified",
  ];

  // Define colors
  const colors = {
    indashyikirwa: chartColors["forest-green"],
    inkomezamihigo: chartColors["sky-blue"],
    inkangurirwamihigo: chartColors["orange"],
    unspecified: chartColors["red"],
    avgScore: chartColors["blue-grey"],
  };

  // Compute totals for all categories across all years
  const numOfYears = data.length;
  const totalValues = {
    avgScore: (
      data.reduce((sum, d) => sum + d.avgScore, 0) / numOfYears
    ).toFixed(2),
    indashyikirwa: (
      data.reduce((sum, d) => sum + d.indashyikirwa, 0) / numOfYears
    ).toFixed(2),
    inkomezamihigo: (
      data.reduce((sum, d) => sum + d.inkomezamihigo, 0) / numOfYears
    ).toFixed(2),
    inkangurirwamihigo: (
      data.reduce((sum, d) => sum + d.inkangurirwamihigo, 0) / numOfYears
    ).toFixed(2),
    unspecified: (
      data.reduce((sum, d) => sum + d.unspecified, 0) / numOfYears
    ).toFixed(2),
  };

  let formattedData = { labels, datasets: [] };

  // Handle different chart types
  if (chartType === "line") {
    formattedData.datasets.push({
      label: `Avg. Score (${totalValues.avgScore})`,
      data: data.map((d) => d.avgScore),
      borderColor: colors.avgScore.border,
      backgroundColor: colors.avgScore.background,
      borderWidth: 2,
      fill: false,
      tension: 0.4,
    });
  }

  if (chartType === "grouped") {
    formattedData.datasets.push({
      type: "line",
      label: `Avg. Score (${totalValues.avgScore})`,
      data: data.map((d) => d.avgScore),
      borderColor: chartColors["blue-grey"].border,
      backgroundColor: chartColors["blue-grey"].background,
      borderWidth: 2,
      fill: false,
      tension: 0.4,
    });

    categories.forEach((category) => {
      formattedData.datasets.push({
        label: `${category.charAt(0).toUpperCase() + category.slice(1)} (${
          totalValues[category]
        })`,
        data: data.map((d) => d[category]),
        backgroundColor: colors[category].background,
        borderColor: colors[category].border,
        borderWidth: 1,
      });
    });
  }

  if (chartType === "pie") {
    formattedData = {
      labels: categories.map(
        (cat) =>
          `${cat.charAt(0).toUpperCase() + cat.slice(1)} (${totalValues[cat]})`
      ),
      datasets: [
        {
          data: categories.map((category) =>
            data.reduce((sum, d) => sum + d[category], 0)
          ),
          backgroundColor: categories.map(
            (category) => colors[category].background
          ),
          borderColor: categories.map((category) => colors[category].border),
          borderWidth: 1,
        },
      ],
    };
  }

  return formattedData;
};

const formatRecruitmentTrendData = (data, chartType = "bar") => {
  if (!data || data.length === 0) return { labels: [], datasets: [] };

  const labels = data.map((item) => item._Year.toString());

  const advertisedData = data.map((item) => item.AdvertisedPosts);
  const hiredData = data.map((item) => item.Hired);

  const colors = [
    {
      background: chartColors["sky-blue"].background,
      border: chartColors["sky-blue"].border,
    },
    {
      background: chartColors["forest-green"].background,
      border: chartColors["forest-green"].border,
    },
  ];

  if (chartType === "bar") {
    return {
      labels,
      datasets: [
        {
          label: "Advertised Posts",
          data: advertisedData,
          backgroundColor: colors[0].background,
          borderColor: colors[0].border,
          borderWidth: 1,
        },
        {
          label: "Hired Employees",
          data: hiredData,
          backgroundColor: colors[1].background,
          borderColor: colors[1].border,
          borderWidth: 1,
        },
      ],
    };
  }

  if (chartType === "combo") {
    return {
      labels,
      datasets: [
        {
          type: "line",
          label: "Hired Employees",
          data: hiredData,
          backgroundColor: colors[1].background,
          borderColor: colors[1].border,
          fill: false,
          tension: 0.3,
          borderWidth: 2,
        },
        {
          type: "bar",
          label: "Advertised Posts",
          data: advertisedData,
          backgroundColor: colors[0].background,
          borderColor: colors[0].border,
          borderWidth: 1,
        },
      ],
    };
  }

  if (chartType === "line") {
    return {
      labels,
      datasets: [
        {
          label: "Advertised Posts",
          data: advertisedData,
          backgroundColor: colors[0].background,
          borderColor: colors[0].border,
          fill: false,
          tension: 0.4,
          borderWidth: 2,
        },
        {
          label: "Hired Employees",
          data: hiredData,
          backgroundColor: colors[1].background,
          borderColor: colors[1].border,
          fill: false,
          tension: 0.4,
          borderWidth: 2,
        },
      ],
    };
  }

  return { labels: [], datasets: [] };
};

const formatLeavePlanTrendData = (data, chartType = "bar") => {
  if (!data || data.length === 0) return { labels: [], datasets: [] };

  const labels = data.map((item) => item.fiscalYearId.toString());

  const plannedData = data.map((item) => item.planedLeaves);
  const takenData = data.map((item) => item.takenLeaves);

  const totalPlanned = plannedData.reduce((sum, v) => sum + v, 0);
  const totalTaken = takenData.reduce((sum, v) => sum + v, 0);

  const colors = [
    {
      background: chartColors["sky-blue"].background,
      border: chartColors["sky-blue"].border,
    },
    {
      background: chartColors["forest-green"].background,
      border: chartColors["forest-green"].border,
    },
  ];

  if (chartType === "bar") {
    return {
      labels,
      datasets: [
        {
          label: `Annual Leaves (Total: ${totalPlanned})`,
          data: plannedData,
          backgroundColor: colors[0].background,
          borderColor: colors[0].border,
          borderWidth: 1,
        },
        {
          label: `Taken Leaves (Total: ${totalTaken})`,
          data: takenData,
          backgroundColor: colors[1].background,
          borderColor: colors[1].border,
          borderWidth: 1,
        },
      ],
    };
  }

  if (chartType === "combo") {
    return {
      labels,
      datasets: [
        {
          type: "line",
          label: `Taken Leaves (Total: ${totalTaken})`,
          data: takenData,
          borderColor: colors[1].border,
          backgroundColor: colors[1].background,
          fill: false,
          tension: 0.4,
          borderWidth: 2,
        },
        {
          type: "bar",
          label: `Annual Leaves (Total: ${totalPlanned})`,
          data: plannedData,
          backgroundColor: colors[0].background,
          borderColor: colors[0].border,
          borderWidth: 1,
        },
      ],
    };
  }

  if (chartType === "line") {
    return {
      labels,
      datasets: [
        {
          label: `Annual Leaves (Total: ${totalPlanned})`,
          data: plannedData,
          borderColor: colors[0].border,
          backgroundColor: colors[0].background,
          fill: false,
          tension: 0.4,
          borderWidth: 2,
        },
        {
          label: `Taken Leaves (Total: ${totalTaken})`,
          data: takenData,
          borderColor: colors[1].border,
          backgroundColor: colors[1].background,
          fill: false,
          tension: 0.4,
          borderWidth: 2,
        },
      ],
    };
  }

  return { labels: [], datasets: [] };
};

const formatTrainingChartData = (rawData, chartType = "bar") => {
  if (!rawData || rawData.length === 0) {
    return {
      labels: [],
      datasets: [],
    };
  }

  const labels = rawData.map((d) => d._Year);
  const approvedData = rawData.map((d) => d.approved);
  const rejectedData = rawData.map((d) => d.rejected);
  const inProgressData = rawData.map((d) => d.inProgress);

  // Colors from your chartColors utils
  const colors = {
    approved: {
      background: chartColors["forest-green"].background,
      border: chartColors["forest-green"].border,
    },
    rejected: {
      background: chartColors["red"].background,
      border: chartColors["red"].border,
    },
    inProgress: {
      background: chartColors["sky-blue"].background,
      border: chartColors["sky-blue"].border,
    },
  };

  if (["bar", "line"].includes(chartType)) {
    return {
      labels,
      datasets: [
        {
          label: `Approved (${approvedData.reduce((a, b) => a + b, 0)})`,
          data: approvedData,
          backgroundColor: colors.approved.background,
          borderColor: colors.approved.border,
          type: chartType,
          fill: chartType !== "line",
          tension: 0.4,
        },
        {
          label: `Rejected (${rejectedData.reduce((a, b) => a + b, 0)})`,
          data: rejectedData,
          backgroundColor: colors.rejected.background,
          borderColor: colors.rejected.border,
          type: chartType,
          fill: chartType !== "line",
          tension: 0.4,
        },
        {
          label: `In Progress (${inProgressData.reduce((a, b) => a + b, 0)})`,
          data: inProgressData,
          backgroundColor: colors.inProgress.background,
          borderColor: colors.inProgress.border,
          type: chartType,
          fill: chartType !== "line",
          tension: 0.4,
        },
      ],
    };
  }

  if (["pie", "doughnut"].includes(chartType)) {
    const totalApproved = approvedData.reduce((a, b) => a + b, 0);
    const totalRejected = rejectedData.reduce((a, b) => a + b, 0);
    const totalInProgress = inProgressData.reduce((a, b) => a + b, 0);

    return {
      labels: ["Approved", "Rejected", "In Progress"],
      datasets: [
        {
          data: [totalApproved, totalRejected, totalInProgress],
          backgroundColor: [
            colors.approved.background,
            colors.rejected.background,
            colors.inProgress.background,
          ],
          borderColor: [
            colors.approved.border,
            colors.rejected.border,
            colors.inProgress.border,
          ],
          borderWidth: 1,
        },
      ],
    };
  }

  return {
    labels: [],
    datasets: [],
  };
};

const formatPayrollChartData = (data, chartType = "bar") => {
  if (!data || data.length === 0) {
    return { labels: [], datasets: [] };
  }

  const labels = data.map((row) => row._Year);
  const rawValues = data.map((row) =>
    parseInt((row.totalGrossSalary || "0").replace(/,/g, ""))
  );

  const colorKeys = Object.keys(chartColors);
  const backgroundColors = labels.map(
    (_, i) => chartColors[colorKeys[i % colorKeys.length]].background
  );
  const borderColors = labels.map(
    (_, i) => chartColors[colorKeys[i % colorKeys.length]].border
  );

  const singleLineColor = chartColors["sky-blue"];

  let formattedData = { labels: labels || [], datasets: [] };

  if (chartType === "bar" || chartType === "line") {
    formattedData.datasets.push({
      label: "Total Gross Salary",
      data: rawValues,
      backgroundColor:
        chartType === "bar" ? singleLineColor.background : undefined,
      borderColor: singleLineColor.border,
      borderWidth: 2,
      fill: chartType === "line",
      tension: chartType === "line" ? 0.4 : 0,
    });
  } else if (["pie", "doughnut", "polar-area"].includes(chartType)) {
    formattedData.datasets.push({
      label: "Total Gross Salary",
      data: rawValues,
      backgroundColor: backgroundColors,
      borderColor: borderColors,
      borderWidth: 1,
    });
  } else {
    formattedData = { labels: labels || [], datasets: [] };
  }

  return formattedData;
};
